import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ShieldCheck, TrendingUp, Users,
  ChevronDown, ChevronUp, Zap, BarChart3, MessageSquare, RefreshCw
} from "lucide-react";

export default function ClinicalIntelligenceView() {
  const [priorityList, setPriorityList] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPatient, setExpandedPatient] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, iRes] = await Promise.all([
        api.get("/clinical-intel/priority-list"),
        api.get("/clinical-intel/insights"),
      ]);
      setPriorityList(pRes.data);
      setInsights(iRes.data);
    } catch (err) {
      toast.error("Erreur chargement intelligence clinique");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const dist = priorityList?.distribution || {};
  const total = priorityList?.total || 0;

  return (
    <div className="space-y-6" data-testid="clinical-intelligence-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-6 h-6 text-teal-600" />
            Intelligence Clinique
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Scoring déterministe basé sur règles — aucune IA
          </p>
        </div>
        <button onClick={fetchAll} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="refresh-intel">
          <RefreshCw className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Distribution Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DistCard label="Haut risque" count={dist.high || 0} total={total} color="red" icon={AlertTriangle} />
        <DistCard label="Risque modéré" count={dist.medium || 0} total={total} color="amber" icon={TrendingUp} />
        <DistCard label="Risque faible" count={dist.low || 0} total={total} color="emerald" icon={ShieldCheck} />
        <DistCard label="Total patients" count={total} total={total} color="slate" icon={Users} />
      </div>

      {/* Insights */}
      {insights?.sentences?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="insights-section">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-amber-500" />
            Insights automatiques
          </h3>
          <div className="space-y-2">
            {insights.sentences.map((s, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                <MessageSquare className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{s}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3 italic">{insights.disclaimer}</p>
        </div>
      )}

      {/* Top Triggered Rules */}
      {insights?.topRules?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="top-rules-section">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-violet-500" />
            Règles les plus déclenchées
          </h3>
          <div className="space-y-2">
            {insights.topRules.map((r, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{r.name}</span>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-500 rounded-full"
                      style={{ width: `${Math.min(r.pct, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-16 text-right">{r.count} ({r.pct}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Priority List */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="priority-list-section">
        <h3 className="font-semibold text-slate-800 mb-3">
          Priorisation clinique ({total} patients)
        </h3>

        {priorityList?.patients?.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Aucun patient actif</p>
        ) : (
          <div className="space-y-1">
            {priorityList.patients.map(p => (
              <div key={p.patientId} data-testid={`priority-patient-${p.patientId}`}>
                <button
                  onClick={() => setExpandedPatient(expandedPatient === p.patientId ? null : p.patientId)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <RiskBadge level={p.riskLevel} />
                    <div>
                      <span className="text-sm font-medium text-slate-800">{p.firstName} {p.lastName}</span>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {p.cancerType && <span>{p.cancerType}</span>}
                        {p.treatmentType && <span>· {p.treatmentType}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">Score: {p.riskScore}</span>
                    {p.recentAlertsCount > 0 && (
                      <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">
                        {p.recentAlertsCount} alertes
                      </span>
                    )}
                    {expandedPatient === p.patientId
                      ? <ChevronUp className="w-4 h-4 text-slate-400" />
                      : <ChevronDown className="w-4 h-4 text-slate-400" />
                    }
                  </div>
                </button>

                {expandedPatient === p.patientId && p.triggeredRules?.length > 0 && (
                  <div className="ml-12 mb-2 space-y-1">
                    {p.triggeredRules.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-1.5 bg-slate-50 rounded text-xs">
                        <RiskBadge level={r.riskLevel} small />
                        <div>
                          <span className="font-medium text-slate-700">{r.name}</span>
                          <span className="text-slate-400 ml-2">(poids: {r.weight})</span>
                          {r.explanation && (
                            <p className="text-slate-500 mt-0.5">{r.explanation}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 mt-3 italic">{priorityList?.disclaimer}</p>
      </div>
    </div>
  );
}


function DistCard({ label, count, total, color, icon: Icon }) {
  const pct = total > 0 ? Math.round(100 * count / total) : 0;
  const colors = {
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const iconColors = { red: "text-red-500", amber: "text-amber-500", emerald: "text-emerald-500", slate: "text-slate-500" };

  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <Icon className={`w-5 h-5 ${iconColors[color]} mb-1`} />
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-xs opacity-70">{label} {pct > 0 && pct < 100 ? `(${pct}%)` : ""}</p>
    </div>
  );
}

function RiskBadge({ level, small }) {
  const styles = {
    HIGH: small ? "bg-red-100 text-red-700" : "px-2.5 py-0.5 bg-red-100 text-red-700",
    MEDIUM: small ? "bg-amber-100 text-amber-700" : "px-2.5 py-0.5 bg-amber-100 text-amber-700",
    LOW: small ? "bg-emerald-100 text-emerald-700" : "px-2.5 py-0.5 bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`${styles[level] || styles.LOW} text-xs font-bold rounded-full ${small ? "px-1.5 py-0.5" : ""}`}>
      {level}
    </span>
  );
}
